"use client"

import { useState } from "react"
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  AppBar,
  Toolbar,
  useTheme,
  useMediaQuery,
  Chip,
  Avatar,
} from "@mui/material"
import { Close as CloseIcon, EmojiEvents as TrophyIcon, Handshake as HandshakeIcon, ChevronRight, ChevronLeft } from "@mui/icons-material"
import MainHeader from "@/components/MainHeader"
import pham from '../../images/pham.png'
import aminudin_zakaria from '../../images/aminudin_zakaria.png'
import angelo from '../../images/angelo.png'
import chong_chiew_fung from '../../images/chong_chiew_fung.png'
import farah_zeehan_othman from '../../images/farah_zeehan_othman.png'
import john from '../../images/john.png'
import maria from '../../images/maria.png'
import muhammad_afta_jadid from '../../images/muhammad_afta_jadid.png'
import muhammad_faishal from '../../images/muhammad_faishal.png'
import rayner_bin_tangkui from '../../images/rayner_bin_tangkui.png'

interface Educator {
  id: string
  name: string
  fullName: string
  title: string
  country: string
  countryCode: string
  photo: string
  projectTitle: string
  quote: string
  profile: string
  background: string
  award: "gold" | "silver" | "bronze" | "collaboration" | "collaboration1" | null
  youtubeId: string
}

const educatorsData: Educator[] = [
  {
    id: "john-roy",
    name: "Engr. John Roy V. Galvez",
    fullName: "Engr. John Roy V. Galvez, LPT, CCPE",
    title: "Special Science Teacher, Camarines Sur National High School",
    country: "Philippines",
    countryCode: "ph",
    photo: john,
    projectTitle: "Project OURALIT - Our Autonomous Reading Assistant for Literacy Intervention and Transformation",
    quote: '"Empowering students through AI, data, and innovation—one breakthrough project at a time."',
    profile:
      "A licensed teacher and computer engineer, Engr. John Roy V. Galvez brings a unique blend of industry expertise and pedagogical passion to his work. As a former Department of Science and Technology (DOST) scholar giving back to his country, he transitioned from a role at Accenture to become a Special Science Teacher. His TCC project, ‘Project OURALIT,’ is an AI-powered reading assistant designed to tackle the Philippines’ critical literacy crisis. This ambitious initiative is grounded in his proven experience mentoring students to create award-winning innovations, including AI-integrated robots and smart learning devices that have earned global recognition.",
    background:
      "B.S. in Computer Engineering (with honors) – Bicol University Polangui Campus, 2019 • Licensed Professional Teacher (LPT), Certified Computer Professional Engineer (CCPE) • Currently pursuing M.S. in Information Technology.",
    award: "gold",
    youtubeId: "vbZzf7BCP8E",
  },
  {
    id: "angelo",
    name: "Mr. Angelo B. Mallari",
    fullName: "Mr. Angelo B. Mallari",
    title: "Junior High School Teacher, Xavier School",
    country: "Philippines",
    countryCode: "ph",
    photo: angelo,
    projectTitle: "AI Co-Intelligence Plug-In",
    quote: '"Forming minds and hearts through dialogue, reflection, and meaningful innovation."',
    profile:
      'An educator at Xavier School specializing in values education, ethics, and reflective practice, Mr. Angelo B. Mallari is deeply committed to human formation. For the Educator TCC Challenge, he proposes the “AI Co-Intelligence Plug-in,” an innovative tool designed to address the passive use of AI. The plug-in transforms AI from an answer-generator into a reflective partner by prompting students with dialogue and self-assessment questions before, during, and after their interaction with AI tools. This project directly embodies his belief that technology must enhance, not replace, uniquely human capacities like critical thinking and ethical judgment.',
    background:
      "Masters in Education, Major in Educational Management (Pamantasan ng Lungsod ng Valenzuela) • Specialization in Values Education, Ethics, and Religious Instruction • Ongoing professional development in Educational Technology & Reflective Pedagogy.",
    award: "silver",
    youtubeId: "LZjP8qSEFoU",
  },
  {
    id: "aminudin",
    name: "Mr. Aminudin Zakaria",
    fullName: "Mr. Aminudin Zakaria",
    title: "Physics & Robotics Teacher, MTsN 9 Jombang, East Java",
    country: "Indonesia",
    countryCode: "id",
    photo: aminudin_zakaria,
    projectTitle: "Responsible AI Use in Physics",
    quote: '"Empowering future-ready learners through hands-on science, immersive tools, and ethical AI integration."',
    profile:
      'A passionate science educator, Aminudin Zakaria teaches Physics and Robotics in East Java and serves as a Research Assistant at Universitas Negeri Surabaya. His TCC project, “Empowering Student Engagement Through Responsible AI Use in Physics Education,” addresses passive AI reliance by training students at his school, MTsN 9 Jombang (a public Islamic junior high school under the Ministry of Religious Affairs), to use AI for critical inquiry. This work is rooted in his advocacy for teacher empowerment through responsible digital integration, especially in underserved regions. He is committed to building a more equitable, engaging, and future-focused science education ecosystem at the intersection of STEM, AI, and immersive learning.',
    background:
      "Bachelor's Degree in Physics Education – Universitas Negeri Surabaya • Research Assistant – Physics Learning Media Innovation Group • Ongoing Development in AI & STEM Pedagogy for Classroom Innovation",
    award: "bronze",
    youtubeId: "ZYCPQVwH1hY",
  },
  {
    id: "rayner",
    name: "Dr. Rayner bin Tangkui",
    fullName: "Dr. Rayner bin Tangkui",
    title: "Lecturer, Institute of Teacher Education – Keningau Campus, Sabah",
    country: "Malaysia",
    countryCode: "my",
    photo: rayner_bin_tangkui,
    projectTitle: "The integration of generative AI as a supporting tool in preservice teachers' reflective practices",
    quote:
      '"Harnessing Minecraft, AI, and creative technologies to make STEM learning dynamic, inclusive, and ethical."',
    profile:
      "Dr. Rayner bin Tangkui is a Malaysian educator and researcher with over 15 years of experience in teacher training and digital integration. At the Institute of Teacher Education in Sabah, he equips preservice teachers to use technologies like Minecraft and ChatGPT to enhance instructional practices. His TCC project, “Fostering Authentic Reflective Thinking Among Preservice Teachers”, guides future educators in using Generative AI (GenAI) as a tool to support – not replace – their reflection processes. Through a structured model combining self-reflection, peer dialogue, and GenAI-supported refinement, his work nurtures ethical, thoughtful, and future-ready educators.",
    background:
      "Ph.D. in Computer in Education – Universiti Malaysia Sabah • Master's and Bachelor's studies in Education / ICT – Universiti Malaysia Sabah • Continuous professional certifications in game-based learning, AI applications, and digital pedagogy.",
    award: "collaboration",
    youtubeId: "W3-zKk5Iixo",
  },
  {
    id: "farah",
    name: "Ms. Farah Zeehan Othman",
    fullName: "Ms. Farah Zeehan Othman",
    title: "Educator, National Institute for Educational Leadership Training (Malaysia)",
    country: "Malaysia",
    countryCode: "my",
    photo: farah_zeehan_othman,
    projectTitle: "Future-proof Leadership Framework - Empowering Ethical Digital Leadership",
    quote: '"Empowering school leaders to lead ethical, impactful digital transformation in the classroom and beyond."',
    profile:
      `Farah Zeehan Othman is a passionate and dedicated Malaysian educator with over 18 years of experience across K–12 education, tertiary, and teacher training. Having taught Mathematics, Science, and Computer Science, she now mentors education leaders at Educational Leadership Training Institute. Her TCC project "Empowering Ethical Digital Leadership in Schools" aims to build a framework of building profiling of Future-Proof Leaders that able to foster ethical digital transformation. Her dedication to impactful teaching and research excellence has earned her international recognition including Best Presenter and Best Paper Awards at global conferences, along with a Gold Medal for innovation at i-RIC 2024. Farah holds multiple certification from Google, Microsoft, Apple, Adobe and Canva underscoring her expertise in the field.`,
    background:
      "BSc Computer Science - University of Technology Malaysia • Master of Science Information Technology - University of Technology Malaysia • Diploma in Education • Ongoing Professional Certification.",
    award: "collaboration",
    youtubeId: "DfS9cbDjPGs",
  },
  {
    id: "muhammad-afta",
    name: "Mr. Muhammad Afta Jadid",
    fullName: "Mr. Muhammad Afta Jadid, S.Pd",
    title: "Physics and Coding Educator, SMA Kharisma Bangsa",
    country: "Indonesia",
    countryCode: "id",
    photo: muhammad_afta_jadid,
    projectTitle: "Integrating AI to enhance understanding of sound waves",
    quote:
      '"Empowering students to turn real-world problems into innovative learning solutions through physics, code, and creativity."',
    profile:
      `Muhammad Afta Jadid is a passionate physics and coding educator at SMA Kharisma Bangsa, currently pursuing a Master's in Physics Education. His TCC project, “Integrating AI to Enhance Understanding of Sound Waves,” transforms passive learning into active creation by guiding students from hands-on experiments with the Phyphox app to building their own AI-powered simulations with Cursor. A mentor for international competitions, including coaching a team to a Genius Olympiad bronze medal, Jadid is also a published researcher and holds the intellectual property rights for a VBA Excel simulation he developed, showcasing his deep commitment to hands-on, innovative science education.`,
    background:
      "Pursuing Master's degree in Physics Education, Universitas Negeri Jakarta (current) • Bachelor's degree in Physics Education, UIN Jakarta • Developed VBA Excel simulation for kinematics (granted intellectual property rights) • Published article “Studi Penggunaan Media Simulasi PhET dalam Pembelajaran Fisika”",
    award: "collaboration",
    youtubeId: "pUPtHBZg4g0",
  },
  {
    id: "chong-chiew-fung",
    name: "Ms. Chong Chiew Fung",
    fullName: "Ms. Chong Chiew Fung",
    title: "STEM Educator, Malaysian Government Secondary Schools",
    country: "Malaysia",
    countryCode: "my",
    photo: chong_chiew_fung,
    projectTitle: "The Amplified Connective Learning Experience or ACLE",
    quote:
      '"Fostering innovation through STEM to shape sustainable and culturally rich futures for learners."',
    profile:
      'Chong Chiew Fung is an accomplished STEM educator at SMK Jambatan Putih, Tawau, Sabah. Her TCC project, the Amplified Connective Learning Experience (ACLE) framework, addresses key challenges in Science education by incorporating real-world applications, hands-on learning and multilingual support to improve student engagement and understanding. Chong has earned several accolades, including a Gold Medal at the 2024 International Sustainability Invention Competition and winning the Southeast Asia STEM Competition 2023. She also served as a judge for the 2023 Southeast Asia STEM Fair and Exposition.',
    background:
      "M.Ed in Education Management • B.Sc. in Science and Education.",
    award: null,
    youtubeId: "4Ej9M6fOeKI",
  },
  {
    id: "muhammad-faishal",
    name: "Mr. Muhammad Faishal",
    fullName: "Mr. Muhammad Faishal",
    title: "School Administrative Officer, MTs Negeri 9 Jombang",
    country: "Indonesia ",
    countryCode: "id",
    photo: muhammad_faishal,
    projectTitle: "SISTA-TISTA: Student Intelligent Assistant & Teacher Intelligent Assistant",
    quote:
      '"Empowering educators with ethical, human-centered AI for smarter, more inclusive learning."',
    profile:
      'Muhammad Faishal combines his background in educational administration with a passion for technological innovation, serving as a School Administrative Officer and an AI-in-education mentor in the Jombang region. His work, which includes collaborating with the Artificial Intelligence Center Indonesia (AICI), is centered on the principle that AI should be used to empower, not replace, human-centered learning. His TCC project, SISTA-TISTA, embodies this philosophy by offering an adaptive student chatbot (SISTA) and an intelligent teacher dashboard (TISTA) to help build a more intelligent, inclusive, and future-ready educational ecosystem.',
    background:
      "Bachelor’s Degree in Educational Administration – Universitas Islam Negeri Sunan Ampel Surabaya (UINSA) • Certified AI for Education Trainer – Artificial Intelligence Center Indonesia (AICI) • Ongoing Participation in National Education Technology Workshops and Policy Dialogues",
    award: null,
    youtubeId: "rxV_YkPE4FI",
  },
  {
    id: "maria-cristina",
    name: "Dr. Maria Cristina P. Arante",
    fullName: "Dr. Maria Cristina P. Arante",
    title: "Head, Learning Innovations and Technology Support, John B. Lacson Foundation Maritime University ",
    country: "Philippines",
    countryCode: "ph",
    photo: maria,
    projectTitle: "AI Integration in Maritime Education",
    quote:
      '"Navigating education through innovation—bridging maritime learning with e-learning and intelligent technologies."',
    profile:
      'Dr. Maria Cristina P. Arante holds a Doctor of Philosophy in Maritime Education (Major in Curriculum & Assessment) and serves as the Head of Learning Innovations and Technology Support at JBLFMU. A passionate advocate for e-learning, her TCC project, ‘Empowering Maritime Educators Through AI,’ is a strategic initiative to assess faculty AI readiness before implementing a phased plan for capacity-building and policy development. A former Board Member of the Philippine eLearning Society, her work aims to institutionalize ethical AI use, solidifying JBLFMU’s pioneering role in tech-enhanced education.',
    background:
      "Ph.D. in Maritime Education (Major in Curriculum and Assessment) – John B. Lacson Foundation Maritime University (2016) • An Educator and a Researcher in e-Learning Methodologies • Board Member (2015–2018), Philippine eLearning Society.",
    award: null,
    youtubeId: "YkqAhrJvI5g",
  },
  {
    id: "ngoc-oanh",
    name: "Ms. Pham Thi Ngoc Oanh",
    fullName: "Ms. Pham Thi Ngoc Oanh",
    title: "Mathematics & Science Support Teacher, St. Nicholas School, Danang City ",
    country: "Vietnam",
    countryCode: "vi",
    photo: pham,
    projectTitle: "Ethical AI use in Education",
    quote:
      '"Bridging science and language through digital learning—empowering students to thrive in bilingual STEM education."',
    profile:
      'A high-honors Biotechnology graduate and a dedicated STEM educator at St. Nicholas School (EQuest Group), Ms. Pham Thi Ngoc Oanh has over six years of experience delivering Math and Science through online American curricula. Her TCC project, ‘Fostering Academic Integrity and Responsible AI Use,’ is a multi-faceted strategy to address student over-reliance on AI. Instead of a single tool, she proposes implementing a clear code of conduct, training teachers in project-based learning, gamification with AI, encouraging reflective writing, and blending digital assessments with traditional paper-based methods. This approach reflects her core philosophy of bridging science and language through a balanced integration of modern technology and proven pedagogy.',
    background:
      "B.S. in Biotechnology (with distinction) – International University, Vietnam National University – Ho Chi Minh City • Certified in Digital Instruction for Bilingual STEM Learning (institution unspecified) • Ongoing professional development in curriculum design and virtual classroom delivery.",
    award: null,
    youtubeId: "dm9hCDzw7qc",
  },
]

const getAwardConfig = (award: string | null) => {
  switch (award) {
    case "gold":
      return { label: "🥇 Gold Award", color: "#FFD700", textColor: "#333" }
    case "silver":
      return { label: "🥈 Silver Award", color: "#C0C0C0", textColor: "#333" }
    case "bronze":
      return { label: "🥉 Bronze Award", color: "#CD7F32", textColor: "#fff" }
    case "collaboration":
      return { label: "🤝 Collaboration Award", color: "#28a745", textColor: "#fff" }
    default:
      return null
  }
}

const getAwardColor = (award: string) => {
  switch (award) {
    case "gold":
      return "#FFD700"
    case "silver":
      return "#C0C0C0"
    case "bronze":
      return "#CD7F32"
    case "collaboration":
      return "#007BFF"
    case "collaboration1":
      return "#007BFF"
    default:
      return "#666"
  }
}

export default function WinnersPage() {
  const [selectedEducator, setSelectedEducator] = useState<Educator | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  const handleOpenModal = (educator: Educator) => {
    setSelectedEducator(educator)
    setModalOpen(true)
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setSelectedEducator(null)
  }

  return (
    <Box>
      <MainHeader />
      <Box sx={{ py: 10, backgroundColor: "#fff" }}>
        <Container maxWidth="md">
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "2.5rem",
              color: "#003366",
              textAlign: "center",
              mb: 2.5,
              fontWeight: "bold",
            }}
          >
            Meet the TCC 2025 Champions
          </Typography>

          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              maxWidth: "700px",
              margin: "0 auto 50px",
              color: "#333",
              lineHeight: 1.6,
            }}
          >
            Ten educators were selected from a highly competitive field to present their groundbreaking ideas at the Final
            Summit. Explore their visionary projects.
          </Typography>

          <Grid container spacing={4}>
            {educatorsData.map((educator) => {
              const awardConfig = getAwardConfig(educator.award)

              return (
                <Grid item xs={12} md={6} key={educator.id}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "10px",
                      boxShadow: "0 5px 20px rgba(0,0,0,0.08)",
                      transition: "transform 0.3s, box-shadow 0.3s",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 8px 25px rgba(0,0,0,0.12)",
                      },
                    }}
                  >
                    {/* <CardMedia
                      component="img"
                      height="400"
                      image={educator.photo}
                      alt={educator.name}
                      sx={{ objectFit: "cover", textAlign: "center" }}
                    /> */}
                    <Avatar src={educator.photo} sx={{
                      width: 150,
                      height: 150,
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "5px solid #ddd",
                      // mb: 3,
                      mt: 2,
                      mx: "auto"
                    }} ></Avatar>

                    <CardContent
                      sx={{
                        p: 3,
                        display: "flex",
                        flexDirection: "column",
                        flexGrow: 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        component="h4"
                        sx={{
                          fontFamily: "Poppins, sans-serif",
                          fontSize: "1.2rem",
                          fontWeight: 700,
                          color: "#003366",
                          mb: 0.5,
                        }}
                      >
                        {educator.name}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.9rem",
                          color: "#666",
                          mb: 2,
                        }}
                      >
                        {educator.country}
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: "1rem",
                          color: "#333",
                          flexGrow: 1,
                          mb: 2.5,
                        }}
                      >
                        {educator.projectTitle}
                      </Typography>

                      {awardConfig && (
                        <Chip
                          label={awardConfig.label}
                          sx={{
                            backgroundColor: awardConfig.color,
                            color: awardConfig.textColor,
                            fontWeight: 700,
                            fontSize: "1rem",
                            borderRadius: "5px",
                            mb: 2,
                            "& .MuiChip-label": {
                              px: 2,
                              py: 1,
                            },
                          }}
                        />
                      )}

                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => handleOpenModal(educator)}
                        sx={{
                          backgroundColor: "#007BFF",
                          color: "#fff",
                          fontWeight: 600,
                          py: 1.5,
                          borderRadius: "5px",
                          textTransform: "none",
                          "&:hover": {
                            backgroundColor: "#0056b3",
                            transform: "translateY(-3px)",
                          },
                        }}
                      >
                        Explore Profile
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              )
            })}
          </Grid>
        </Container>

        {/* Modal Dialog */}
        <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="lg" fullWidth fullScreen={isMobile}>
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="h6">Educator Profile</Typography>
                  <IconButton onClick={handleCloseModal}>
                    <CloseIcon />
                  </IconButton>
          </DialogTitle>
          <DialogContent>
            {selectedEducator && (
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  {selectedEducator.youtubeId != "" && (
                    <Box
                      sx={{
                        width: "100%",
                        height: 0,
                        paddingBottom: "56.25%", // Tỉ lệ 16:9
                        position: "relative",
                        borderRadius: 1,
                        overflow: "hidden",
                      }}
                    >
                      <iframe
                        width="100%"
                        height="100%"
                        src={`https://www.youtube.com/embed/${selectedEducator.youtubeId}`}
                        title="YouTube video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                        }}
                      ></iframe>
                    </Box>
                  )}
                  
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <Avatar src={selectedEducator.photo} alt={selectedEducator.name} sx={{ width: 80, height: 80 }} />
                    <Box>
                      <Typography variant="h5">{selectedEducator.fullName}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedEducator.title}
                      </Typography>
                      {selectedEducator.award && selectedEducator.award !== "collaboration" && (
                        <Chip
                          label={`${selectedEducator.award.charAt(0).toUpperCase() + selectedEducator.award.slice(1)} Award`}
                          sx={{
                            bgcolor: getAwardColor(selectedEducator.award),
                            color: "white",
                            mt: 1,
                          }}
                        />
                      )}
                    </Box>
                  </Box>
  
                  <Box sx={{ bgcolor: "#e9ecef", p: 2, borderRadius: 1, mb: 2 }}>
                    <Typography variant="body2">
                      <strong>TCC Project:</strong> {selectedEducator.projectTitle}
                    </Typography>
                  </Box>
  
                  <Box sx={{ bgcolor: "#f8f9fa", p: 2, borderLeft: "4px solid #007BFF", mb: 2 }}>
                    <Typography variant="body1" sx={{ fontStyle: "italic" }}>
                      {selectedEducator.quote}
                    </Typography>
                  </Box>
  
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "#003366", borderBottom: "2px solid #003366", pb: 1 }}
                  >
                    Professional Profile
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {selectedEducator.profile}
                  </Typography>
  
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "#003366", borderBottom: "2px solid #003366", pb: 1 }}
                  >
                    Academic Background
                  </Typography>
                  <Box component="ul" sx={{ listStyleType: "none", p: 0, m: 0 }}>
                    {selectedEducator.background.split("•").map((item, index) => (
                      <Box component="li" key={index} sx={{ mb: 1, display: "flex", alignItems: "flex-start" }}>
                        <Typography variant="body2" sx={{ mr: 1 }}>
                          &bull;
                        </Typography>
                        <Typography variant="body2">{item.trim()}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            )}
          </DialogContent>
        </Dialog>
      </Box>
      {/* Footer */}
      <Box sx={{ py: 3, bgcolor: "#f8f9fa", textAlign: "center", borderTop: "1px solid #ddd" }}>
        <Container>
          <Typography variant="body2" color="text.secondary">
            © 2025 Pythaverse. All Rights Reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  )
}
